import { getTierBadgeVariant } from "../-utils/badge-variants"
import { AddCreditDialog } from "./-components/add-credit-dialog"
import { EditUserDialog } from "./-components/edit-user-dialog"
import { DeleteUserDialog } from "./-components/delete-user-dialog"
import { IssueSubscriptionDialog } from "./-components/issue-subscription-dialog"
import { IssueTicketDialog } from "./-components/issue-ticket-dialog"
import { LedgerTable } from "./-components/ledger-table"
import { TicketsTable } from "./-components/tickets-table"
import { Subscriptions } from "./-components/subscriptions"
import { Teams } from "./-components/teams"
import { useState } from "react"
import { createFileRoute, Link } from "@tanstack/react-router"
import { useQuery } from "convex/react"
import { ArrowLeft, Pencil, Trash2 } from "lucide-react"
import { api } from "@convex/_generated/api"
import { formatDateInLocalTimezone } from "@/lib/timezone-utils"
import type { Id } from "@convex/_generated/dataModel"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"

export const Route = createFileRoute("/(admin)/admin/users/$userId")({
  component: RouteComponent,
})

function RouteComponent() {
  const { userId } = Route.useParams()
  const [editOpen, setEditOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)

  const userDetails = useQuery(api.users.handlers.getUserDetails, {
    userId: userId as Id<"users">,
  })
  const transactions = useQuery(api.ledger.handlers.getTransactionHistory, {
    userId: userId as Id<"users">,
  })

  if (userDetails === undefined || transactions === undefined) {
    return <div className="p-4">Loading...</div>
  }

  const { user, balance, activeSubscription } = userDetails

  // Type guard for subscription tier
  const isValidTier = (tier: unknown): tier is "bronze" | "silver" | "gold" => {
    return tier === "bronze" || tier === "silver" || tier === "gold"
  }

  // Calculate running balance for transactions
  let runningBalance = 0
  const transactionsWithBalance = transactions.map((tx) => {
    if (tx.type === "credit") {
      runningBalance += tx.amount
    } else {
      runningBalance -= tx.amount
    }
    return { ...tx, runningBalance }
  })

  // Get user initials
  const initials = user.name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()

  return (
    <div className="flex flex-1 flex-col gap-6 p-4 md:p-6">
      {/* Header with Back Button and Actions */}
      <div className="flex items-center justify-between">
        <Link
          to="/admin/users"
          search={{ search: "" }}
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Users
        </Link>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setEditOpen(true)
            }}
          >
            <Pencil className="mr-2 h-4 w-4" />
            Edit User
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={() => {
              setDeleteOpen(true)
            }}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Delete User
          </Button>
        </div>
      </div>

      {/* User Profile Card */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-6">
            {/* Left: User Image & Basic Info */}
            <div className="flex gap-4 flex-1">
              <Avatar className="h-20 w-20">
                <AvatarImage src={user.imageUrl} alt={user.name} />
                <AvatarFallback className="text-2xl">{initials}</AvatarFallback>
              </Avatar>
              <div className="space-y-2 flex-1">
                <div>
                  <h1 className="text-2xl font-bold">{user.name}</h1>
                  <p className="text-sm text-muted-foreground">
                    {user.email ?? "No email"}
                  </p>
                </div>
                <div className="text-sm pt-2">
                  <span className="text-muted-foreground">Joined:</span>{" "}
                  <span className="font-medium">
                    {formatDateInLocalTimezone(new Date(user._creationTime).toISOString())}
                  </span>
                </div>
              </div>
            </div>

            {/* Right: Subscription */}
            {activeSubscription &&
              typeof activeSubscription === "object" &&
              "tier" in activeSubscription &&
              "quarter" in activeSubscription &&
              isValidTier(activeSubscription.tier) && (
                <div className="flex flex-col gap-4 md:min-w-[240px] border-t md:border-t-0 md:border-l pt-4 md:pt-0 md:pl-6">
                  <div>
                    <p className="text-sm text-muted-foreground mb-2">
                      Active Subscription
                    </p>
                    <div className="space-y-2">
                      <Badge
                        variant={getTierBadgeVariant(activeSubscription.tier)}
                        className="text-base"
                      >
                        {activeSubscription.tier.toUpperCase()}
                      </Badge>
                      <p className="text-xs text-muted-foreground">
                        {(
                          activeSubscription.quarter as { name?: string } | null
                        )?.name ?? "Unknown Quarter"}
                      </p>
                    </div>
                  </div>
                </div>
              )}
          </div>
        </CardContent>
      </Card>

      {/* Transaction Ledger */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Transaction Ledger</CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                Credits received and charges issued for subscriptions & tickets
              </p>
            </div>
            <div className="flex items-center gap-2">
              <AddCreditDialog userId={userId as Id<"users">} />
              <IssueSubscriptionDialog userId={userId as Id<"users">} />
              <IssueTicketDialog userId={userId as Id<"users">} />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <LedgerTable
            transactions={transactionsWithBalance}
            balance={balance}
          />
        </CardContent>
      </Card>

      {/* Subscriptions & Tickets Summary */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Subscriptions */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Subscriptions</CardTitle>
          </CardHeader>
          <CardContent>
            <Subscriptions userId={userId as Id<"users">} />
          </CardContent>
        </Card>

        {/* Tickets */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Tickets</CardTitle>
          </CardHeader>
          <CardContent>
            <TicketsTable userId={userId as Id<"users">} />
          </CardContent>
        </Card>
      </div>

      {/* Teams */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Teams</CardTitle>
        </CardHeader>
        <CardContent>
          <Teams userId={userId as Id<"users">} />
        </CardContent>
      </Card>

      {/* Dialogs */}
      <EditUserDialog user={user} open={editOpen} onOpenChange={setEditOpen} />
      <DeleteUserDialog
        user={user}
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
      />
    </div>
  )
}
